import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { ArrowLeft, EnvelopeSimple, SpinnerGap, CheckCircle } from "phosphor-react";
import { API_URL } from "../auth";

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/auth/forgot-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim().toLowerCase() }),
      });

      if (response.ok) {
        setSubmitted(true);
      } else {
        // Still show success message to not leak user existence
        setSubmitted(true);
      }
    } catch (error) {
      console.error("Forgot password error:", error);
      // Still show success to not leak info
      setSubmitted(true);
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#1A1A1A] flex items-center justify-center p-4">
        <div className="fixed inset-0 grain-texture pointer-events-none" />

        <div className="relative z-10 w-full max-w-md">
          <Link
            to="/login"
            className="inline-flex items-center gap-2 text-zinc-400 hover:text-white mb-8 transition-colors"
          >
            <ArrowLeft size={20} />
            <span>Back to login</span>
          </Link>

          <div className="bg-white border-2 border-zinc-200 p-8 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle size={32} className="text-green-600" weight="fill" />
            </div>

            <h1 className="font-heading text-2xl font-bold uppercase text-zinc-900 mb-2">
              Check Your Email
            </h1>
            <p className="text-zinc-600 mb-6">
              If an account exists for <strong>{email}</strong>, we've sent a password reset link.
            </p>
            <p className="text-sm text-zinc-500 mb-6">
              Didn't receive an email? Check your spam folder or make sure you entered the correct email address.
            </p>

            <Button
              onClick={() => navigate("/login")}
              className="w-full h-12 bg-[#FF6600] hover:bg-[#CC5200] text-white font-heading uppercase tracking-wide"
              data-testid="back-to-login-btn"
            >
              Return to Login
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#1A1A1A] flex items-center justify-center p-4">
      <div className="fixed inset-0 grain-texture pointer-events-none" />

      <div className="relative z-10 w-full max-w-md">
        <Link
          to="/login"
          className="inline-flex items-center gap-2 text-zinc-400 hover:text-white mb-8 transition-colors"
        >
          <ArrowLeft size={20} />
          <span>Back to login</span>
        </Link>

        <div className="bg-white border-2 border-zinc-200 p-8">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-[#FF6600]/10 rounded-full flex items-center justify-center mx-auto mb-6">
              <EnvelopeSimple size={32} className="text-[#FF6600]" />
            </div>
            
            <h1 className="font-heading text-2xl font-bold uppercase text-zinc-900 mb-2">
              Forgot Password?
            </h1>
            <p className="text-zinc-600">
              Enter your email address and we'll send you a link to reset your password.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="email" className="text-zinc-700">
                Email Address
              </Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                className="mt-1 h-12 border-2 border-zinc-200 focus:border-[#FF6600]"
                required
                data-testid="forgot-email-input"
              />
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 bg-[#FF6600] hover:bg-[#CC5200] text-white font-heading uppercase tracking-wide"
              data-testid="forgot-submit-btn"
            >
              {loading ? (
                <>
                  <SpinnerGap size={20} className="animate-spin mr-2" />
                  Sending...
                </>
              ) : (
                "Send Reset Link"
              )}
            </Button>
          </form>

          <p className="text-center text-sm text-zinc-500 mt-6">
            Remember your password?{" "}
            <Link
              to="/login"
              className="text-[#FF6600] font-medium hover:underline"
            >
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
